/* eslint-disable no-restricted-syntax */
import layerService from '@/services/layer-service'
import { isActive } from '@/services/feature-flag-service'
import Layer from '@/fish/components/layers/fish-layer'

/** Class responsible for handling layer-groups */
export default class LayerGroup {
  /**
   * @constructor
   */
  constructor(groupData) {
    this.id = groupData.unique_code
    this.name = groupData.name
    this.type = groupData.type
    this.description = groupData.description
    this.thumbnail = groupData.thumbnail_url
    this.rawLayers = groupData.layers
    this.active = groupData.show_by_default || false
    this.hasAccess = this.rawLayers[0]?.has_access ?? false
    this.layers = []

    // Add to layer service
    layerService.addLayer(this)

    // Set the current active state of the group
    this.setGroupActiveState()
    this.updateActiveLayers()

    // Parse the group's layers
    this.parseLayers()

    // Turn on active groups
    if (this.active) {
      this.toggleGroup(true)
    }
  }

  /**
   * Parse the group's layers
   */
  parseLayers() {
    // FUTURE - update once basemaps move into this layer system
    const isBasemap = false
    const isSublayer = true

    for (const rawLayer of this.rawLayers) {
      const layer = new Layer(rawLayer, isSublayer, isBasemap, this.active)
      this.layers.push(layer)
    }
  }

  /**
   * Updates the group active state based on saved settings and defaults
   */
  setGroupActiveState() {
    const layersToEnable = layerService.getModeLayersToEnable()

    const requiredLayers = layerService.getAlwaysOnModeLayersToEnable()
    const layerPref = layerService.getLayerPreference(this.id.toString())

    // Layer preference exists so use saved setting
    if (layerPref) {
      // Overwrite the user's layer pref if it is a required-on layer
      if (requiredLayers.includes(this.id.toString()) && this.hasAccess) {
        this.active = true
      }
      // Overwrite the setting if the associated mode is not enabled
      else if (
        !layersToEnable.includes(this.id.toString()) ||
        !this.hasAccess
      ) {
        this.active = false
      } else {
        this.active = layerPref.active
      }
    }
    // Layer is brand new so we need to use the default setting and save
    else {
      this.updatePreferences(this.active)

      // Overwrite the setting if the associated mode is not enabled
      if (!layersToEnable.includes(this.id.toString()) || !this.hasAccess) {
        this.active = false
      }
    }
  }

  /**
   * Toggle the group on and off the map
   *
   * @param {Bool} active - whether we're enabling or disabling the group
   */
  toggleGroup(active) {
    if (!this.hasAccess) {
      return
    }

    // Check if layer has a feature gate
    const featureGateKey = layerService.getModeLayerFeatureKey(this.id)
    if (featureGateKey && !isActive(featureGateKey)) {
      return
    }

    this.active = active
    this.updateActiveLayers()

    // Toggle the children
    for (const layer of this.layers) {
      active ? layer.addToMap() : layer.removeFromMap()
    }

    if (active) {
      this.updatePreferences(active)
    }
  }

  /**
   * Update the group in layer preferences
   *
   * @param {bool} active - active state of the layer
   */
  updatePreferences(active) {
    layerService.updateLayerPreferences(this.id, active)
  }

  /**
   * Update the layer service active layers
   */
  updateActiveLayers() {
    this.active
      ? layerService.addActiveLayer(this.id)
      : layerService.removeActiveLayer(this.id)
  }
}
